import React, { useMemo, useState } from "react";
import { FadeUpGroup } from "../components/FadeUpGroup";
import { Rss, Sparkles } from "lucide-react";
import { CHANGELOG } from "../lib/changelog";
import { PRODUCTS } from "../lib/products";

const fmtDate = (iso) => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("en-US", {
        day: "numeric",
        month: "long",
        year: "numeric",
    });
};

// Render a tiny subset of markdown: **bold** + plain text.
const renderInline = (text) => {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) =>
        part.startsWith("**") && part.endsWith("**") ? (
            <strong key={i}>{part.slice(2, -2)}</strong>
        ) : (
            <React.Fragment key={i}>{part}</React.Fragment>
        )
    );
};

const productBySlug = Object.fromEntries(PRODUCTS.map((p) => [p.slug, p]));

const ChangelogPage = () => {
    const tags = useMemo(() => {
        const s = new Set();
        CHANGELOG.forEach((e) => e.tag && s.add(e.tag));
        return ["All", ...Array.from(s).sort()];
    }, []);
    const [activeTag, setActiveTag] = useState("All");

    const entries = useMemo(() => {
        const filtered =
            activeTag === "All"
                ? CHANGELOG
                : CHANGELOG.filter((e) => e.tag === activeTag);
        return [...filtered].sort((a, b) => (a.date < b.date ? 1 : -1));
    }, [activeTag]);

    return (
        <section
            className="section"
            id="changelog"
            data-testid="changelog-page"
        >
                    <div className="container">
                        <div className="section-head">
                            <span className="eyebrow">Changelog</span>
                            <h1 className="h1" style={{ marginTop: 18 }}>
                                What we shipped, week by week.
                            </h1>
                            <p>
                                A public log of every meaningful change to Scitus —
                                product launches, infrastructure, content, and the
                                occasional bug fix worth bragging about. Newest at the
                                top.
                            </p>
                        </div>

                        {/* Filter chips */}
                        <div className="changelog-filters" data-testid="changelog-filters">
                            {tags.map((tag) => (
                                <button
                                    key={tag}
                                    type="button"
                                    className={`changelog-chip ${
                                        tag === activeTag ? "active" : ""
                                    }`}
                                    onClick={() => setActiveTag(tag)}
                                    data-testid={`changelog-chip-${tag
                                        .toLowerCase()
                                        .replace(/\s+/g, "-")}`}
                                >
                                    {tag}
                                </button>
                            ))}
                        </div>

                        {/* Timeline */}
                        <FadeUpGroup>
                            <ol
                                className="changelog-list"
                                data-testid="changelog-list"
                            >
                                {entries.map((entry) => (
                                    <li
                                        key={entry.slug}
                                        className="changelog-entry fade-up"
                                        data-testid={`changelog-entry-${entry.slug}`}
                                    >
                                        <div className="changelog-rail" aria-hidden="true">
                                            <span className="changelog-dot" />
                                        </div>
                                        <article className="changelog-card">
                                            <header className="changelog-card-head">
                                                <time
                                                    dateTime={entry.date}
                                                    className="changelog-date"
                                                >
                                                    {fmtDate(entry.date)}
                                                </time>
                                                {entry.tag && (
                                                    <span
                                                        className={`changelog-tag changelog-tag--${entry.tag
                                                            .toLowerCase()
                                                            .replace(/\s+/g, "-")}`}
                                                    >
                                                        {entry.tag}
                                                    </span>
                                                )}
                                            </header>
                                            <h2 className="changelog-title">
                                                {entry.title}
                                            </h2>
                                            {entry.body.map((para, i) => (
                                                <p key={i} className="changelog-body">
                                                    {renderInline(para)}
                                                </p>
                                            ))}
                                            {entry.products && entry.products.length > 0 && (
                                                <div className="changelog-products">
                                                    {entry.products.map((slug) => {
                                                        const p = productBySlug[slug];
                                                        if (!p) return null;
                                                        return (
                                                            <a
                                                                key={slug}
                                                                href={`/products/${slug}`}
                                                                className="changelog-product-chip"
                                                                data-testid={`changelog-product-${slug}`}
                                                            >
                                                                {p.name}
                                                            </a>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </article>
                                    </li>
                                ))}
                            </ol>
                        </FadeUpGroup>

                        {/* Subscribe nudge */}
                        <div
                            className="changelog-subscribe"
                            data-testid="changelog-subscribe"
                        >
                            <div>
                                <Sparkles size={18} style={{ color: "var(--accent)" }} />
                                <h3 className="h3" style={{ fontSize: "1.25rem" }}>
                                    Want this in your inbox?
                                </h3>
                                <p className="muted" style={{ marginTop: 6 }}>
                                    Monthly Operator Insights includes the highlights from
                                    this changelog plus operator-grade reading. No fluff,
                                    one click to unsubscribe.
                                </p>
                            </div>
                            <a
                                href="/#newsletter"
                                className="btn btn-primary"
                                data-testid="changelog-subscribe-cta"
                            >
                                <Rss size={14} /> Subscribe
                            </a>
                        </div>
                    </div>
                </section>
    );
};

export default ChangelogPage;
